import apiClient from './api';

// Loyalty & Rewards API (staff JWT). Every call unwraps the {success, data}
// envelope and throws the server's own sentence on failure, so screens toast
// the reason a till was refused rather than "request failed".
const unwrap = ({ data }) => {
  if (!data?.success) throw new Error(data?.error || 'Request failed.');
  return data.data;
};
const fail = (err) => {
  // Axios buries the server's sentence under response.data.error.
  throw new Error(err.response?.data?.error || err.message || 'Request failed.');
};
const get = (url, params) => apiClient.get(url, { params }).then(unwrap, fail);
const post = (url, body) => apiClient.post(url, body || {}).then(unwrap, fail);
const put = (url, body) => apiClient.put(url, body || {}).then(unwrap, fail);
const del = (url) => apiClient.delete(url).then(unwrap, fail);

const loyaltyService = {
  // Programme + dashboard
  getProgram: () => get('/loyalty/program/'),
  saveProgram: (body) => put('/loyalty/program/', body),
  getSummary: () => get('/loyalty/summary/'),

  // Tiers
  listTiers: () => get('/loyalty/tiers/'),
  createTier: (body) => post('/loyalty/tiers/', body),
  saveTier: (id, body) => put(`/loyalty/tiers/${id}/`, body),
  deleteTier: (id) => del(`/loyalty/tiers/${id}/`),

  // Earn rules (promotions layer)
  listRules: () => get('/loyalty/rules/'),
  createRule: (body) => post('/loyalty/rules/', body),
  saveRule: (id, body) => put(`/loyalty/rules/${id}/`, body),
  deleteRule: (id) => del(`/loyalty/rules/${id}/`),

  // Members
  lookup: (q) => get('/loyalty/lookup/', { q }),
  listMembers: (params) => get('/loyalty/members/', params),
  memberDetail: (id) => get(`/loyalty/members/${id}/`),
  enrol: (body) => post('/loyalty/members/enrol/', body),
  adjustPoints: (id, points, reason) =>
    post(`/loyalty/members/${id}/adjust/`, { points, reason }),
  setMemberStatus: (id, status) =>
    post(`/loyalty/members/${id}/status/`, { status }),
  updateMember: (id, body) => post(`/loyalty/members/${id}/update/`, body),
  // The till: pin a member to an open bill / spend points as money on it
  attachOrder: (orderId, accountId) =>
    post('/loyalty/attach-order/', { order_id: orderId, account_id: accountId }),
  redeemCurrency: (accountId, body) =>
    post(`/loyalty/members/${accountId}/redeem-currency/`, body),

  // Self-service sign-ups
  listLeads: (status) => get('/loyalty/leads/', status ? { status } : undefined),
  convertLead: (id) => post(`/loyalty/leads/${id}/convert/`),
  discardLead: (id) => post(`/loyalty/leads/${id}/discard/`),

  // Rewards catalogue + claiming
  listRewards: (activeOnly) =>
    get('/loyalty/rewards/', activeOnly ? { active: '1' } : undefined),
  createReward: (body) => post('/loyalty/rewards/', body),
  saveReward: (id, body) => put(`/loyalty/rewards/${id}/`, body),
  deleteReward: (id) => del(`/loyalty/rewards/${id}/`),
  catalogueForMember: (accountId, locationId) =>
    get(`/loyalty/members/${accountId}/catalogue/`,
      locationId ? { location_id: locationId } : undefined),
  claimReward: (accountId, rewardId, locationId) =>
    post(`/loyalty/members/${accountId}/claim/`,
      { reward_id: rewardId, location_id: locationId || undefined }),

  // Vouchers
  listVouchers: (params) => get('/loyalty/vouchers/', params),
  voucherLookup: (code) => get('/loyalty/vouchers/lookup/', { code }),
  redeemVoucher: (body) => post('/loyalty/vouchers/redeem/', body),
  voidVoucher: (id, body) => post(`/loyalty/vouchers/${id}/void/`, body),

  // Gift cards
  listGiftCards: (params) => get('/loyalty/gift-cards/', params),
  issueGiftCard: (body) => post('/loyalty/gift-cards/issue/', body),
  giftCardLookup: (code) => get('/loyalty/gift-cards/lookup/', { code }),
  redeemGiftCard: (body) => post('/loyalty/gift-cards/redeem/', body),
  topUpGiftCard: (body) => post('/loyalty/gift-cards/top-up/', body),
  voidGiftCard: (id, note) => post(`/loyalty/gift-cards/${id}/void/`, { note }),
};

export default loyaltyService;
